/**
 * Initial state for the basic game (rule.md §3). Deterministic: no RNG — the
 * start layout is the fixed canonical mirror-symmetric deployment, plus the
 * second mover's first-player-edge compensation (one free Deploy).
 */
import {
  AP_PER_TURN,
  HOME_CELLS,
  STACK_HP,
  START_POSITIONS,
  START_RESERVE,
} from "./data";
import { type Faction, PLAYER_IDS, type PlayerId } from "./types/common";
import type {
  AugmentedWarState,
  Board,
  PlayerState,
  Unit,
} from "./types/game-state";

export interface InitGameConfig {
  readonly gameId?: string;
  /** Faction per player (default: beast). */
  readonly factions?: Partial<Record<PlayerId, Faction>>;
  /** Who moves first (default: p1). */
  readonly firstPlayerId?: PlayerId;
  /**
   * Initiative compensation (rule.md §3): under zoned ZoC the **first mover** is
   * at a disadvantage (defense/second-mover is favored), so the first mover starts
   * with one free Deploy (5 deployed / 5 reserve). Default `true`; set `false` for
   * a symmetric setup (analysis / tests).
   */
  readonly compensateFirstMover?: boolean;
}

function createBoard(): Record<string, Unit> {
  const board: Record<string, Unit> = {};
  for (const player of PLAYER_IDS) {
    for (const cell of START_POSITIONS[player]) {
      board[cell] = { owner: player, hp: STACK_HP };
    }
  }
  return board;
}

function createPlayer(faction: Faction): PlayerState {
  return {
    faction,
    reserve: START_RESERVE,
    spentElements: [],
    bastionAvailable: faction === "federation",
  };
}

export function initGame(config: InitGameConfig = {}): AugmentedWarState {
  const factions = config.factions ?? {};
  const firstPlayerId = config.firstPlayerId ?? "p1";
  const board = createBoard();
  const players: Record<PlayerId, PlayerState> = {
    p1: createPlayer(factions.p1 ?? "beast"),
    p2: createPlayer(factions.p2 ?? "beast"),
  };

  // Initiative compensation: the first mover gets one free Deploy (rule.md §3) —
  // zoned ZoC favors the second mover, so the first mover is compensated.
  if (config.compensateFirstMover !== false) {
    const home = HOME_CELLS[firstPlayerId].find((h) => !board[h]);
    if (home && players[firstPlayerId].reserve > 0) {
      board[home] = { owner: firstPlayerId, hp: STACK_HP };
      players[firstPlayerId] = {
        ...players[firstPlayerId],
        reserve: players[firstPlayerId].reserve - 1,
      };
    }
  }

  return {
    gameId: config.gameId ?? "augmented-war",
    phase: "PLAYING",
    board: board as Board,
    currentPlayerId: firstPlayerId,
    turn: 1,
    apRemaining: AP_PER_TURN,
    actionsUsed: { move: 0, attack: 0, deploy: 0 },
    players,
  };
}
